import { useMemo, useRef, useState, useLayoutEffect } from 'react'
import { useFrame, extend, useThree } from '@react-three/fiber'
import { useScroll, Instances, Instance, Float, Sparkles, Text, Stars, Torus, shaderMaterial } from '@react-three/drei'
import * as THREE from 'three'

// --- DATA ---
const TUNNEL_CARDS = [
  { top: "BEM-VINDO", title: "OVERPIXEL\nWORLD", sub: "Prepare-se para uma jornada imersiva onde aprendizado e diversão se encontram." },
  { top: "MUNDO IMERSIVO", title: "EXPERIÊNCIA\nIMERSIVA 3D", sub: "Um mundo completo em realidade virtual, web e mobile. Entre e explore cada detalhe." },
  { top: "ESTAMPARIA", title: "DOMINE A\nESTAMPARIA", sub: "Aprenda DTF, sublimação e técnicas profissionais de impressão em um ambiente gamificado." },
  { top: "MÓDULOS", title: "MÓDULOS\nINTELIGENTES", sub: "Sistema modular progressivo que adapta o aprendizado ao seu ritmo e nível de conhecimento." },
  { top: "IA ASSISTENTE", title: "INTELIGÊNCIA\nARTIFICIAL", sub: "Nossa IA te guia, corrige erros em tempo real e sugere as melhores estratégias para cada desafio." },
  { top: "PRODUÇÃO", title: "SIMULE SUA\nPRODUÇÃO", sub: "Gerencie pedidos, coordene equipes e otimize processos como em uma empresa real." },
  { top: "VENDAS", title: "ESTRATÉGIAS\nDE VENDAS", sub: "Aprenda precificação, negociação e atendimento ao cliente de forma prática e divertida." },
  { top: "COORDENAÇÃO", title: "GESTÃO &\nCOORDENAÇÃO", sub: "Organize fluxos de trabalho, distribua tarefas e gerencie prazos com eficiência máxima." },
  { top: "DESAFIOS", title: "DESAFIOS\nREALISTAS", sub: "Enfrente situações do dia a dia: máquinas quebradas, prazos apertados e clientes exigentes." },
  { top: "PROGRESSO", title: "DO ZERO AO\nSUCESSO", sub: "Construa seu império da estamparia do iniciante ao empresário de sucesso." },
  { top: "FRUSTRAÇÃO", title: "VAI SER\nFRUSTRANTE", sub: "Haverá erros, falhas e momentos difíceis. Mas isso faz parte do processo." },
  { top: "PERSISTÊNCIA", title: "NUNCA\nDESISTA", sub: "A persistência é o que separa quem aprende de quem domina. Continue sempre." },
  { top: "EVOLUÇÃO", title: "EVOLUÇÃO\nCONTÍNUA", sub: "Cada erro é uma lição. Cada desafio é uma oportunidade de crescimento." },
  { top: "DIVERSÃO", title: "APRENDA\nBRINCANDO", sub: "Gamificação total: conquistas, rankings, recompensas e competições." },
  { top: "IMERSÃO TOTAL", title: "PERCA-SE NO\nMUNDO", sub: "Esqueça o relógio. Aqui você vai passar horas explorando e dominando cada detalhe." },
  { top: "REALISMO", title: "FÍSICA E COMPORTAMENTO\nREALISTAS", sub: "Máquinas que funcionam como no mundo real. Latência zero e precisão absoluta." },
  { top: "COMUNIDADE", title: "CONECTE-SE\nGLOBALMENTE", sub: "Jogue com outros empreendedores, troque experiências e cresça junto com a comunidade." },
  { top: "TECNOLOGIA", title: "INFRAESTRUTURA\nDE PONTA", sub: "Processamento distribuído garante latência zero e criatividade infinita. Cada pixel renderizado em alta performance." },
  { top: "PREPARADO?", title: "VOCÊ ESTÁ\nPREPARADO?", sub: "É hora de entrar. Sua jornada começa agora." },
  { top: "PORTAL FINAL", title: "ENTRANDO\nOVERPIXEL WORLD", sub: "3... 2... 1..." }
]

// --- GEOMETRIC ASSETS ---

// Instanced Server Racks for performance
function ServerInstances({ count = 200, curve }: { count: number, curve: THREE.CatmullRomCurve3 }) {
  const instances = useMemo(() => {
    const temp = []
    // Start generating servers only after the entrance (approx index 2 of points, or t > 0.1)
    for (let i = 0; i < count; i++) {
      const t = 0.15 + (Math.random() * 0.85) // Skip first 15% (Approach)
      const point = curve.getPointAt(t)
      const tangent = curve.getTangentAt(t)
      const normal = new THREE.Vector3(-tangent.z, 0, tangent.x).normalize()
      
      // Offset lateral
      const side = Math.random() > 0.5 ? 1 : -1
      const distance = 25 + Math.random() * 50
      
      const pos = point.clone().add(normal.clone().multiplyScalar(distance * side))
      pos.y += (Math.random() - 0.5) * 40
      
      const lookAt = point.clone()
      lookAt.y = pos.y
      
      temp.push({ pos, lookAt, scale: [2 + Math.random()*4, 10 + Math.random()*30, 2 + Math.random()*4] })
    }
    return temp
  }, [curve, count])

  return (
    <Instances range={count}>
      <boxGeometry />
      <meshStandardMaterial color="#0f172a" emissive="#0ea5e9" emissiveIntensity={0.5} roughness={0.2} metalness={0.8} />
      {instances.map((data, i) => (
        <Instance
          key={i}
          position={data.pos}
          scale={data.scale as any}
          onUpdate={(self) => {
             self.lookAt(data.lookAt)
          }}
        />
      ))}
    </Instances>
  )
}

function FloatingInstance({ position, scale }: any) {
    const ref = useRef<any>()
    useFrame((state) => {
        if(ref.current) {
            ref.current.rotation.x += 0.02
            ref.current.rotation.y += 0.03
        }
    })
    return <Instance ref={ref} position={position} scale={[scale, scale, scale]} />
}

function DataPackets({ count = 100, curve }: { count: number, curve: THREE.CatmullRomCurve3 }) {
    const instances = useMemo(() => {
        const temp = []
        for (let i = 0; i < count; i++) {
            const t = 0.15 + (Math.random() * 0.85) // Skip approach
            const point = curve.getPointAt(t)
            const offset = new THREE.Vector3((Math.random()-0.5)*15, (Math.random()-0.5)*15, (Math.random()-0.5)*15)
            const pos = point.clone().add(offset)
            temp.push({ pos, scale: Math.random() * 0.5 })
        }
        return temp
    }, [curve, count])

    return (
        <Instances range={count}>
            <octahedronGeometry args={[1, 0]} />
            <meshBasicMaterial color="#a855f7" wireframe />
            {instances.map((data, i) => (
                <FloatingInstance key={i} position={data.pos} scale={data.scale} />
            ))}
        </Instances>
    )
}

function GlowingLabel({ text }: { text: string }) {
    const meshRef = useRef<any>(null)
    const [color] = useState(() => new THREE.Color())
    
    useFrame((state) => {
        if (meshRef.current) {
            const t = state.clock.elapsedTime
            // Cycle HSL for rainbow effect
            color.setHSL((t * 0.5) % 1, 1, 0.6)
            meshRef.current.color = color
            // Pulse opacity
            meshRef.current.fillOpacity = 0.8 + Math.sin(t * 8) * 0.2
        }
    })

    return (
        <group position={[0, 4, 0]}>
            {/* Texto Principal com Glow Interno */}
            <Text
                ref={meshRef}
                fontSize={2}
                font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYAZ9hjp-Ek-_EeA.woff"
                anchorX="center"
                anchorY="bottom"
                outlineWidth={0.05}
                outlineColor="white"
                outlineBlur={0.2}
                fillOpacity={1}
                renderOrder={100}
            >
                {text}
            </Text>

            {/* Texto de Fundo para efeito de Neon Bloom mais forte */}
            <Text
                position={[0, 0, -0.1]}
                fontSize={2}
                font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYAZ9hjp-Ek-_EeA.woff"
                anchorX="center"
                anchorY="bottom"
                color={color}
                fillOpacity={0.5}
                outlineWidth={0.2}
                outlineColor={color}
                outlineBlur={0.5}
                renderOrder={99}
            >
                {text}
            </Text>
        </group>
    )
}

function SmartCard({ card }: { card: any }) {
    const groupRef = useRef<THREE.Group>(null)
    const [isFollowing, setIsFollowing] = useState(false)
    
    // Store original transformation
    const originalPos = useMemo(() => card.position.clone(), [card.position])
    
    useFrame((state) => {
        if (!groupRef.current) return

        const camera = state.camera
        const distToOriginal = camera.position.distanceTo(originalPos)
        
        // Trigger follow when close (approx 50 units)
        // Release follow when dragged too far (approx 120 units from original spot)
        // Check dot product to ensure we only trigger when approaching from front/start
        // (Optional but good for not triggering when scrolling back up)
        
        if (!isFollowing) {
            // Trigger follow when very close (approx 40 units)
            if (distToOriginal < 40) {
                 setIsFollowing(true)
            }
            // Lerp back to original faster (0.2) so it clears the way
            groupRef.current.position.lerp(originalPos, 0.2)
            // Always look at camera for readability (Billboard effect) even when stationary
            groupRef.current.lookAt(camera.position) 
        } else {
            // Check release condition - Release sooner (70 units) to avoid overlap with next card
            if (distToOriginal > 70) {
                setIsFollowing(false)
            } else {
                // FOLLOW MODE
                // Target position: In front of camera
                const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion)
                const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion)
                
                // Position: 8 units in front (closer), and centered vertically (0 instead of -1.5)
                const targetPos = camera.position.clone()
                    .add(forward.multiplyScalar(8))
                    .add(up.multiplyScalar(0))
                
                groupRef.current.position.lerp(targetPos, 0.1)
                groupRef.current.lookAt(camera.position) // Look at camera
            }
        }
    })

    return (
        <group ref={groupRef} position={card.position}>
             <Float speed={2} floatIntensity={1} rotationIntensity={0.2}>
                {/* 
                   Rotação removida para corrigir espelhamento com lookAt(camera)
                   Scale mantido em 0.10
                */}
                <group scale={0.10}>
                    {/* Top Label - Neon Glowing Component */}
                    <GlowingLabel text={card.top} />
                    
                    {/* Main Title - Mantido em -2 */}
                    <Text 
                        position={[0, -2, 0]} 
                        fontSize={3} 
                        color="white" 
                        anchorX="center" 
                        anchorY="middle"
                        textAlign="center"
                        maxWidth={20}
                        font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYAZ9hjp-Ek-_EeA.woff"
                        outlineWidth={0.05}
                        outlineColor={card.color}
                        fillOpacity={1}
                        renderOrder={100}
                    >
                        {card.title}
                    </Text>
                    
                    {/* Subtitle - Ajuste fino: -7 (era -8) */}
                    <Text 
                        position={[0, -6, 0]} 
                        fontSize={0.8} 
                        color="#cbd5e1" 
                        anchorX="center" 
                        anchorY="top"
                        textAlign="center"
                        maxWidth={15}
                        font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYAZ9hjp-Ek-_EeA.woff"
                        fillOpacity={0.9}
                        renderOrder={100}
                    >
                        {card.sub}
                    </Text>
                </group>
             </Float>
        </group>
    )
}

function TunnelCards({ curve }: { curve: THREE.CatmullRomCurve3 }) {
  const cards = useMemo(() => {
    const count = TUNNEL_CARDS.length
    const startT = 0.05 
    const endT = 0.95
    
    return TUNNEL_CARDS.map((data, i) => {
      const progress = startT + (i / (count - 1)) * (endT - startT)
      const point = curve.getPointAt(progress)
      const tangent = curve.getTangentAt(progress)
      
      const up = new THREE.Vector3(0, 1, 0)
      const normal = new THREE.Vector3().crossVectors(tangent, up).normalize()
      
      const side = i % 2 === 0 ? -1 : 1

      const offsetDistance = 0 
      
      const position = point.clone()
      position.y += 0 
      
      const lookAtPoint = curve.getPointAt(Math.min(progress + 0.01, 1))
      lookAtPoint.y = position.y
      
      return { ...data, position, lookAtPoint, color: side === 1 ? "#22d3ee" : "#a855f7" }
    })
  }, [curve])

  return (
    <group>
      {cards.map((card, i) => (
        <SmartCard key={i} card={card} />
      ))}
    </group>
  )
}

function EntranceGate({ position, lookAt }: { position: THREE.Vector3, lookAt: THREE.Vector3 }) {
    const ref = useRef<THREE.Group>(null)
    useFrame((state) => {
        if(ref.current) {
            ref.current.rotation.z -= 0.01
        }
    })
    
    return (
        <group position={position} ref={ref}>
             {/* Main Ring */}
            <Torus args={[25, 1, 16, 100]}>
                <meshStandardMaterial color="#000" emissive="#22d3ee" emissiveIntensity={2} />
            </Torus>
            {/* Outer Ring Rotating */}
            <Torus args={[30, 0.5, 16, 50]} rotation={[0,0,1]}>
                 <meshStandardMaterial color="#000" emissive="#a855f7" emissiveIntensity={2} />
            </Torus>
        </group>
    )
}

// --- BLACK HOLE SHADER ---
const BlackHoleMaterial = shaderMaterial(
  {
    uTime: 0,
    uColor: new THREE.Color('#000000'),
    uRimColor: new THREE.Color('#4f46e5'), // Indigo
  },
  // Vertex Shader
  `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vViewPosition;

    void main() {
      vUv = uv;
      vNormal = normalize(normalMatrix * normal);
      vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
      vViewPosition = -mvPosition.xyz;
      gl_Position = projectionMatrix * mvPosition;
    }
  `,
  // Fragment Shader
  `
    uniform float uTime;
    uniform vec3 uColor;
    uniform vec3 uRimColor;
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vViewPosition;

    void main() {
      // Fresnel effect for event horizon glow
      vec3 viewDir = normalize(vViewPosition);
      float fresnel = dot(viewDir, vNormal);
      fresnel = clamp(1.0 - fresnel, 0.0, 1.0);
      fresnel = pow(fresnel, 4.0); // Sharp rim

      // Dynamic distortion/swirl on the surface (optional visual flair)
      // For a pure black hole, center is black.
      
      vec3 finalColor = mix(uColor, uRimColor * 2.0, fresnel);
      
      // Make center pure black, only rim glows
      gl_FragColor = vec4(finalColor, 1.0);
    }
  `
)
extend({ BlackHoleMaterial })

function BlackHoleEntrance({ position }: { position: THREE.Vector3 }) {
  const meshRef = useRef<any>(null)
  const accretionRef = useRef<any>(null)
  const shockwaveRef = useRef<any>(null)

  useFrame((state) => {
    const t = state.clock.elapsedTime
    if (meshRef.current) {
        meshRef.current.uTime = t
    }
    if (accretionRef.current) {
        accretionRef.current.rotation.z = t * 0.5
        // Pulsing scale
        const scale = 1 + Math.sin(t * 2) * 0.05
        accretionRef.current.scale.set(scale, scale, 1)
    }
    if (shockwaveRef.current) {
         // Ray shockwave rotation
         shockwaveRef.current.rotation.z = -t * 2.0
         shockwaveRef.current.rotation.x = Math.PI / 2
    }
  })

  return (
    <group position={position} rotation={[Math.PI / 2, 0, 0]}>
      {/* 1. Event Horizon (Sphere) */}
      <mesh scale={[8, 8, 8]}>
        <sphereGeometry args={[1, 64, 64]} />
        {/* @ts-ignore */}
        <blackHoleMaterial ref={meshRef} transparent />
      </mesh>

      {/* 2. Accretion Disk (Flat Ring with texture/noise) */}
      <mesh ref={accretionRef}>
        <ringGeometry args={[10, 25, 64]} />
        <meshBasicMaterial 
            color="#8b5cf6" 
            side={THREE.DoubleSide} 
            transparent 
            opacity={0.6} 
            blending={THREE.AdditiveBlending}
        />
      </mesh>
      
      {/* 3. Shockwave Ray (Conical gradient) */}
      <mesh ref={shockwaveRef} position={[0, 0, -20]} rotation={[Math.PI/2, 0, 0]}>
         <cylinderGeometry args={[2, 15, 80, 32, 1, true]} />
         <meshBasicMaterial 
            color="#00ffff" 
            transparent 
            opacity={0.1} 
            side={THREE.DoubleSide} 
            blending={THREE.AdditiveBlending} 
            depthWrite={false}
         />
      </mesh>
      
       {/* 4. Particle Horizon */}
       <Sparkles count={200} scale={40} size={4} speed={0.4} opacity={1} color="#ffffff" />
    </group>
  )
}

const ENVIRONMENTS = [
  { // 0: Deep Space Entry (Manter consistência com a entrada)
    bg: '#050011',
    fog: '#050011',
    tunnel: '#22d3ee' // Cyan Original
  },
  { // 1: Cyber Data (Matrix)
    bg: '#050011', // Mantendo fundo escuro padrão
    fog: '#050011',
    tunnel: '#00ff41' // Matrix Green
  },
  { // 2: Nebula Heat (Magma/Energia)
    bg: '#050011', // Mantendo fundo escuro padrão
    fog: '#050011',
    tunnel: '#ff3300' // Red/Orange Neon
  },
  { // 3: Quantum Freeze (Gelo/Tech)
    bg: '#050011', // Mantendo fundo escuro padrão
    fog: '#050011',
    tunnel: '#00ffff' // Cyan Ice
  },
  { // 4: Overpixel Core (Roxo/Dourado - Chegada)
    bg: '#050011', // Mantendo fundo escuro padrão
    fog: '#050011',
    tunnel: '#ffd700' // Gold
  }
]

function TunnelEnvironmentManager({ tunnelMatRef }: { tunnelMatRef: any }) {
  const { scene } = useThree()
  
  useFrame((state) => {
    // 1. Manter Background e Fog fixos (Deep Space)
    // Isso garante a "ambiência" que o usuário gostou
    const deepSpaceColor = new THREE.Color('#050011')
    
    // Só aplica se ainda não estiver (otimização leve)
    // Mas como o CinematicBackground pode brigar, forçamos suavemente ou assumimos que o CinematicBackground cuida disso.
    // O usuário pediu "aumenta o background", então vamos garantir que o fog não corte a visão no fundo
    if (scene.fog) {
        // @ts-ignore
        scene.fog.color.copy(deepSpaceColor)
        // @ts-ignore
        scene.fog.far = 4000 // Aumenta MUITO a distância do fog para ver "lá para baixo"
    }
    
    // 2. Animação de Cor Contínua do Túnel (Rainbow Cycle)
    if (tunnelMatRef.current) {
         const t = state.clock.elapsedTime
         // Ciclo RGB contínuo e suave
         // Velocidade 0.5 para não ser epilético, mas dinâmico
         const hue = (t * 0.1) % 1 
         tunnelMatRef.current.color.setHSL(hue, 1, 0.5)
         
         // Pulsar opacidade
         tunnelMatRef.current.opacity = 0.3 + Math.sin(t * 2) * 0.1
    }
  })
  
  return null
}

export function TechTunnel() {
  const scroll = useScroll()
  const [isVisible, setIsVisible] = useState(false)
  
  // --- PATH DEFINITION ---
  // Adjusted to match EXACTLY where ScrollManager leaves off (Y=-100 at scroll 0.21)
  const startY = -100 

  useFrame(() => {
    // Safety check for scroll
    if (!scroll) return

    // Otimização: Só atualiza estado se mudar
    // Ativa quando passa de 21% do scroll (ponto de entrada)
    if (scroll.offset > 0.21 && !isVisible) setIsVisible(true)
    if (scroll.offset <= 0.21 && isVisible) setIsVisible(false)
  }) 
  
  // Points for the CAMERA path (Includes Approach)
  // We want a smooth transition from Vertical Drop (Station) to Horizontal Flight (Tunnel)
  const curvePoints = useMemo(() => [
      new THREE.Vector3(0, startY, 0),         // 0. Start exactly where ScrollManager drops us (Y=-100)
      new THREE.Vector3(0, startY - 20, 0),    // 1. Vertical Shaft (Y=-120)
      new THREE.Vector3(0, startY - 40, 0),    // 2. Deep inside shaft (Y=-140)
      new THREE.Vector3(0, startY - 60, -20),  // 3. Curve Start (Y=-160, Z=-20)
      new THREE.Vector3(0, startY - 70, -80),  // 4. Horizontal Entry
      new THREE.Vector3(0, startY - 70, -400), 
      new THREE.Vector3(50, startY - 50, -800), 
      new THREE.Vector3(80, startY - 90, -1200), 
      new THREE.Vector3(0, startY - 70, -1600), 
      new THREE.Vector3(-100, startY, -2000), 
      new THREE.Vector3(0, startY + 50, -2600),
  ], [])

  const curve = useMemo(() => new THREE.CatmullRomCurve3(curvePoints, false, 'catmullrom', 0.2), [curvePoints])

  // --- TUNNEL GEOMETRY ---
  // Tunnel starts EARLIER now to encompass the drop
  const tunnelCurve = useMemo(() => {
      // Include the vertical drop points (from index 1)
      const points = curvePoints.slice(1)
      // Add a point slightly above to close the top visual
      points.unshift(new THREE.Vector3(0, startY + 20, 0))
      return new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.2)
  }, [curvePoints])

  // Radius increased to 6 to match SpaceStation tube
  // Segments increased to 16 to match SpaceStation tube
  const tubeGeo = useMemo(() => new THREE.TubeGeometry(tunnelCurve, 300, 6, 16, false), [tunnelCurve])
  // REMOVED cageGeo to clear view
  // const cageGeo = useMemo(() => new THREE.TubeGeometry(tunnelCurve, 150, 30, 16, false), [tunnelCurve])

  // Ref para suavizar o alvo do lookAt (evita giros bruscos que desconectam o visual)
  const currentLookAt = useRef(new THREE.Vector3(0, startY, 0))
  const tunnelMatRef = useRef<THREE.MeshBasicMaterial>(null)

  useFrame((state) => {
    // Safety check for scroll - CRITICAL FIX
    if (!scroll) return

    // Start exactly when Intro releases (0.21 for 32 pages)
    const start = 0.21
    const end = 1
    const range = end - start

    // Visibility logic
    // Safe access to offset
    const currentOffset = scroll.offset || 0
    
    if (currentOffset > 0.21 && !isVisible) setIsVisible(true)
    if (currentOffset <= 0.21 && isVisible) setIsVisible(false)
    
    if (currentOffset > start) {
      const progress = (currentOffset - start) / range
      
      if (progress >= 0 && progress <= 1) {
        const point = curve.getPointAt(progress)
        const lookAtPoint = curve.getPointAt(Math.min(progress + 0.01, 1))
        
        // Suavizei a interpolação para 0.05 para ficar mais "sutil" e cinematográfico
        state.camera.position.lerp(point, 0.05)
        
        // CORREÇÃO: Suavizar também a rotação (LookAt)
        // Isso impede que a câmera gire instantaneamente enquanto o corpo ainda está viajando
        // Mantém os cards e o túnel "conectados" visualmente
        currentLookAt.current.lerp(lookAtPoint, 0.05)
        state.camera.lookAt(currentLookAt.current)
        
        // Dynamic FOV
        const targetFov = 50 + (Math.sin(progress * Math.PI) * 15)
        state.camera.fov = THREE.MathUtils.lerp(state.camera.fov, targetFov, 0.05)
        state.camera.updateProjectionMatrix()
      }
    }
  })

  return (
    <group visible={isVisible}>
      {/* 1. Black Hole Entrance */}
      <BlackHoleEntrance position={new THREE.Vector3(0, startY - 20, 0)} />

      {/* 2. Environment Manager (Controla cores e fog) */}
      <TunnelEnvironmentManager tunnelMatRef={tunnelMatRef} />

      {/* 3. The Rail (Inside Tunnel Only) */}
      <mesh geometry={tubeGeo}>
        <meshBasicMaterial ref={tunnelMatRef} color="#22d3ee" transparent opacity={0.3} wireframe />
      </mesh>

      {/* 4. Cage Removed */}

      {/* 6. Content - ServerInstances REMOVED */}
      <DataPackets count={200} curve={curve} />
      
      {/* 6.5 Tunnel Cards (New) */}
      <TunnelCards curve={tunnelCurve} />

      {/* 7. Environment */}
      <Stars radius={300} depth={100} count={3000} factor={4} saturation={0} fade speed={1} />
      
      <Sparkles 
        count={2000} // Reduced from 5000
        scale={[200, 200, 2500]}
        size={6}
        speed={2}
        opacity={0.5}
        color="#22d3ee"
        position={[0, -100, -1200]}
      />
      
    </group>
  )
}
